import React, { useState, useCallback } from 'react';
import { Button, Table, message } from 'antd';
import ReuploadModal from '../ReuploadModal';
import { API_ORIGIN } from '../../../config';
import { formatDate } from '../../../utils/directOrderStatus';

// Admin view: every direct order that has lost its attachments.
const AttachmentsPanel = () => {
  const [missingAttachments, setMissingAttachments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [missingAttachmentsLoading, setMissingAttachmentsLoading] = useState(false);
  const [reuploadOrder, setReuploadOrder] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [reuploadLoading, setReuploadLoading] = useState(false);

  const fetchMissingAttachments = useCallback(async () => {
    setMissingAttachmentsLoading(true);
    try {
      const response = await fetch(`${API_ORIGIN}/api/direct-order/missing-attachments`);
      const data = await response.json();
      if (response.ok && data.success) {
        setMissingAttachments(data.directOrders || []);
        setLoaded(true);
        message.success(`Found ${data.count} direct order(s) with missing attachments`);
      } else {
        message.error(data.error || 'Failed to fetch missing attachments');
      }
    } catch (error) {
      message.error('Failed to fetch missing attachments');
    } finally {
      setMissingAttachmentsLoading(false);
    }
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 10) {
      message.error('Maximum 10 files allowed');
      return;
    }
    setUploadingFiles(files);
  };

  const closeReupload = () => {
    setReuploadOrder(null);
    setUploadingFiles([]);
  };

  const handleReuploadAttachments = async () => {
    if (!reuploadOrder || uploadingFiles.length === 0) {
      message.error('Please select files to upload');
      return;
    }

    setReuploadLoading(true);
    try {
      const formData = new FormData();
      uploadingFiles.forEach((file) => formData.append('attachments', file));

      const response = await fetch(`${API_ORIGIN}/api/direct-order/reupload-attachments/${reuploadOrder.doId}`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data.success) {
        message.success(`Attachments replaced for ${data.doRef}`);
        closeReupload();
        fetchMissingAttachments();
      } else {
        message.error(data.error || 'Failed to re-upload attachments');
      }
    } catch (error) {
      message.error('Failed to re-upload attachments');
    } finally {
      setReuploadLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'doId', key: 'doId', width: 80 },
    { title: 'Reference', dataIndex: 'doRef', key: 'doRef', render: (text) => <span className="do-ref">{text}</span> },
    { title: 'Date', dataIndex: 'doDate', key: 'doDate', render: formatDate },
    { title: 'Supplier', dataIndex: 'doSupplier', key: 'doSupplier' },
    {
      title: '',
      key: 'action',
      align: 'right',
      render: (_, record) => <Button onClick={() => setReuploadOrder(record)}>Replace attachments</Button>,
    },
  ];

  return (
    <section className="do-panel do-table">
      <div className="do-panel__head">
        <div>
          <h2 className="do-panel__title">Orders with missing attachments</h2>
          <p className="do-panel__hint">Check for orders whose files are gone, then upload replacements.</p>
        </div>
        <Button type="primary" onClick={fetchMissingAttachments} loading={missingAttachmentsLoading}>
          {loaded ? 'Refresh' : 'Check now'}
        </Button>
      </div>
      {loaded && missingAttachments.length === 0 ? (
        <div className="do-empty">
          <strong>All attachments are in place</strong>
          No direct orders are missing files.
        </div>
      ) : !loaded ? (
        <div className="do-empty">Run a check to see which orders are missing attachments.</div>
      ) : (
        <Table
          dataSource={missingAttachments.map((order, index) => ({ ...order, key: order.doId || index }))}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={missingAttachments.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        />
      )}

      <ReuploadModal
        order={reuploadOrder}
        files={uploadingFiles}
        onFilesChange={handleFileChange}
        onOk={handleReuploadAttachments}
        onCancel={closeReupload}
        loading={reuploadLoading}
      />
    </section>
  );
};

export default AttachmentsPanel;
